<template>
  <div id="dashboard">
    <div class="card">
      <p>Total cards: {{ stats.cardCount }}</p>
      <p>Active cards: {{ stats.cardCountActive }}</p>
      <p>Cards due now: {{ stats.cardCountDue }}</p>
      <p>Learning cards due: {{ stats.cardCountLearning }}</p>
      <p>Cards waiting for first occurrence: {{ stats.newCards }}</p>
    </div>

    <div class="card">
      <h2>Score per Date</h2>

      <BarChart class="chart" :data-set="scoresForChart" :margin-left="40" :margin-top="40" :tick-count="5"
        :bar-padding="0.5" />
    </div>

    <div class="card" v-if="false">
      <h2>Due Cards per Date</h2>
      <p>Shows the lowest count for each day</p>

      <BarChart class="chart" :data-set="duesForChart" :margin-left="40" :margin-top="40" :tick-count="5"
        :bar-padding="0.5" />
    </div>

    <div class="card" v-if="scoresForChart.length > 0 && dailyGoal">
      <h2>Daily Goal</h2>
      <label for="daily-progress"></label>
      <meter id="daily-progress" style="width: 100%" min="0" :max="dailyGoal" :value="scoresForChart[10][1]">
        {{ scoresForChart[10][1] }} out of {{ dailyGoal }} XP
      </meter>
      <p>
        {{ scoresForChart[10][1] }} out of {{ dailyGoal }} XP reached.
        <span v-if="scoresForChart[10][1] >= dailyGoal">Nice!</span>
        <span v-else>Keep going...</span>
      </p>
      <hr />
      <div class="flex-center">
        <input class="input" type="range" min="50" max="1000" step="10" v-model="dailyGoal"
          :disabled="!dailyGoalEditable" />
        <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"
          @click="setDailyGoalEditState">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z">
          </path>
        </svg>
      </div>
      <p>Set daily goal: {{ dailyGoal }}</p>
    </div>

    <div class="card">
      <h2>Progress Rate</h2>
      <p>
        Averaged over the last 10 active days, you have a progress rate of: <em>{{ dueRate }}</em>
      </p>
      <p>That means that each day, you have about {{ Math.floor(Math.abs(dueRate)) }}
        <span v-if="dueRate < 1">less</span>
        <span v-else>more</span>
        due cards than on the day before.
      </p>

      <p v-if="Math.floor(stats.cardCountDue / (dueRate * (-1))) > 0">
        At this rate, you will reach zero due cards in about {{ Math.floor(stats.cardCountDue / (dueRate * (-1))) }} days.
      </p>

    </div>

    <div class="card">
      <button @click.prevent="$store.dispatch('signOutAction')" class="button">
        Logout
      </button>
    </div>

    <div class="card">
      <h2>Import A Deck</h2>
      <p class="normal-text">Importing Cards is under construction.</p>
      <vue-csv-import v-model="csv" :fields="{
        front: { required: true, label: 'Front' },
        back: { required: false, label: 'Back' },
        type: { required: false, label: 'Type' },
        interval: { required: false, label: 'Interval' },
        unit: { required: false, label: 'Unit' },
        tags: { required: false, label: 'Tags' },
      }">
        <vue-csv-errors></vue-csv-errors>
        <vue-csv-input></vue-csv-input>
        <vue-csv-map></vue-csv-map>
      </vue-csv-import>
      <div v-if="csv.length > 0 && !uploadOngoing">
        <input class="input" type="text" v-model="collectionName" placeholder="Name Of Collection" />
        <button class="button" @click="generateCollection">
          Create Collection From Data
        </button>
      </div>
      <div v-if="uploadOngoing">...uploading</div>
    </div>
  </div>
</template>

<script>
import { uuid } from "vue-uuid";
import BarChart from "@/components/BarChart.vue";
import { supabase } from "../supabase";
import Toastify from "toastify-js";
import "toastify-js/src/toastify.css";
import regression from 'regression';

export default {
  data() {
    return {
      csv: [],
      collectionName: "",
      headers: true,
      uploadOngoing: false,
      scoresForChart: [],
      duesForChart: [],
      dailyGoal: null,
      dailyGoalEditable: false,
      dueRate: null,
    };
  },
  components: {
    BarChart,
  },
  async mounted() {
    this.$store.dispatch("getStatistics").then(() => {
      this.saveDueCount();
    });
    try {
      const { data, error } = await supabase.rpc("scores_per_date", {
        id_of_user: supabase.auth.currentUser.id,
      });
      if (error) {
        throw error;
      }

      // flatten objects in data array to an array with the format ["2022-03-02", 7]

      // get the dates for the last 10 days
      let maxValue = 0;
      var getDaysArray = function (startDay, endDay) {
        for (
          var arr = [], dt = new Date(startDay);
          dt <= endDay;
          dt.setDate(dt.getDate() + 1.1)
        ) {

          arr.push(new Date(dt));
        }
        return arr;
      };
      // get the date 10 days ago
      var getDaysAgo = function (days) {
        return new Date(new Date().getTime() - days * 24 * 60 * 60 * 1000);
      };
      const startDay = getDaysAgo(10);
      const endDay = getDaysAgo(-1);

      const days = getDaysArray(startDay, endDay);

      let scoreData = [];
      for (const day of days) {
        const date = day.toISOString().substring(0, 10);
        const score = data.find((score) => score.scoredate === date);
        if (score) {
          scoreData.push([date, score.score]);
        } else {
          scoreData.push([date, 0]);
        }
      }


      this.scoresForChart = scoreData.map((obj) => {
        return [obj[0].substring(5, 10), obj[1]];
      });

    } catch (error) {

    }

    // get daily goal data from supabase
    try {
      const { error, data } = await supabase
        .from("profiles")
        .select()
        .match({ user_id: supabase.auth.currentUser.id });
      if (error) throw error;
      this.dailyGoal = data[0].daily_goal;
    } catch (error) {
      Toastify({
        text:
          "Something went wrong with fetching profile/daily goal data \n" +
          error.error_description ||
          error.message ||
          error,
        duration: 3000,
        newWindow: true,
        close: true,
        gravity: "top", // `top` or `bottom`
        position: "center", // `left`, `center` or `right`
        stopOnFocus: true, // Prevents dismissing of toast on hover
        style: {
          background: "var(--primary-500)",
        },
      }).showToast();
    }

    // get due cards from supabase
    try {
      const { error, data } = await supabase
        .from("cards_due")
        .select()
        .order("date", { ascending: false })
        .match({ user_id: supabase.auth.currentUser.id })
        .limit(10);
      if (error) throw error;

      this.duesForChart = data.map((obj) => {
        return [new Date(obj.created_at).getTime() / 1000 / 86400, obj.count];
      });

      const result = regression.linear(this.duesForChart);
      const gradient = result.equation[0];

      this.dueRate = gradient;
    } catch (error) {
      Toastify({
        text:
          "Something went wrong with fetching daily due cards data \n" +
          error.error_description ||
          error.message ||
          error,
        duration: 3000,
        newWindow: true,
        close: true,
        gravity: "top", // `top` or `bottom`
        position: "center", // `left`, `center` or `right`
        stopOnFocus: true, // Prevents dismissing of toast on hover
        style: {
          background: "var(--primary-500)",
        },
      }).showToast();
    }



  },
  methods: {
    getDateFromTime(time) {
      return `${new Date(time).getFullYear()}-${new Date(time).getMonth() + 1
        }-${new Date(time).getDate()}`;
    },
    getDaytime(time) {
      return `${new Date(time).getHours()}`;
    },
    async saveDueCount() {

      // save date as yyyy/mm/dd
      const date = new Date().toISOString().substring(0, 10);
      // check if there already is a record in 'card_due' for this user and date
      const { data, error } = await supabase
        .from("cards_due")
        .select()
        .eq("date", date)
        .match({ user_id: supabase.auth.currentUser.id });
      if (error) {
        throw error;
      }
      if (data.length > 0) {

        // update the record
        await supabase
          .from("cards_due")
          .update({
            date: date,
            user_id: supabase.auth.currentUser.id,
            count: this.stats.cardCountDue,
          })
          .eq("date", date)
          .match({ user_id: supabase.auth.currentUser.id });
      } else {
        // create a new record
        await supabase.from("cards_due").insert(
          {
            date: date,
            user_id: supabase.auth.currentUser.id,
            count: this.stats.cardCountDue,
          },
          { returning: "minimal" }
        );
      }
    },
    async generateCollection() {
      this.uploadOngoing = true;
      const packId = uuid.v4();
      const newCollection = {
        id: packId,
        name: this.collectionName,
        description: "Imported Collection",
        userId: supabase.auth.currentUser.id,
        public: false,
        emoji: "🗞️",
      };
      await this.$store.dispatch("addCollection", newCollection);
      // skip the first entry, as it is the header

      for (const card of this.csv) {
        const id = uuid.v4()
        const newCard = {
          user_id: supabase.auth.currentUser.id,
          id: id,
          type: card.type || "standard",
          front: card.front,
          back: card.back || "",
          nextShown: new Date() / 1000,
          ease: 0,
          interval: card.interval || 1,
          repetition: 0,
          unit: card.unit || "days",
          pack_id: packId,
        };
        if (newCard.front !== "front") {
          this.$store.dispatch("addCard", newCard);
        }
        // tags come as a string ['tag1', 'tag2'], make them an array, discard duplicates
        console.log('card tags', card.tags)
        let tagList = card.tags
          .replace("[", "")
          .replace("]", "")
          .split(" ")

        console.log('preliminary tag list', tagList)
        tagList = tagList
          .map((tag) => tag.trim())
          .filter((tag, index, self) => self.indexOf(tag) === index);
        console.log('tag list', tagList)

        for (const tag of tagList) {
          const newTag = {
            name: tag,
            user_id: supabase.auth.currentUser.id,
            id: uuid.v4(),
          };
          await this.$store.dispatch("addTag", newTag)
          // get id of tag from supabase, may not be the tag we just created
          const { data, error } = await supabase
            .from("tags")
            .select("id")
            .eq("name", tag)
            .match({ user_id: supabase.auth.currentUser.id });
          if (error) {
            throw error;
          }
          const tag_id = data.id;
          this.$store.dispatch("addTagToCard", {
            card_id: id,
            tag_id: tag_id,
          });
        }
      }
      this.uploadOngoing = false;
      this.collectionName = "";
    },
    async setDailyGoalEditState() {
      this.dailyGoalEditable = !this.dailyGoalEditable;

      // if we just switched off edit state, send data to supabase
      if (!this.dailyGoalEditable) {

        try {
          const { error } = await supabase
            .from("profiles")
            .update(
              [
                {
                  daily_goal: this.dailyGoal,
                },
              ],
              { returning: "minimal" }
            )
            .match({ user_id: supabase.auth.currentUser.id });
          if (error) throw error;
        } catch (error) {
          Toastify({
            text:
              "Something went wrong. Try again or reload the page. \n Error trying to edit daily goal: \n" +
              error.error_description ||
              error.message ||
              error,
            duration: 3000,
            newWindow: true,
            close: true,
            gravity: "top", // `top` or `bottom`
            position: "center", // `left`, `center` or `right`
            stopOnFocus: true, // Prevents dismissing of toast on hover
            style: {
              background: "var(--primary-500)",
            },
          }).showToast();
        }
      }
    },
  },
  computed: {
    stats() {
      return this.$store.state.statistics;
    },
  },
};
</script>
